import { Flex, Skeleton } from 'antd'
import IconValueDown from 'components/icons/IconValueDown'
import IconValueUp from 'components/icons/IconValueUp'
import {
  GROWTH_PERFORMANCE_TREE_OPTIONS,
  METRIC_OPTIONS,
  MULTIPLE_TREE_OPTIONS,
  OVERALL_PERFORMANCE_TREE_OPTIONS,
  PERFORMANCE_STATUS_TREE_OPTIONS,
  PIE_TREE_OPTIONS,
  SINGLE_TREE_OPTIONS
} from 'constants/common'
import { API_KEY } from 'constants/react-query'
import { selectQuarterByStartFiscalYear } from 'constants/translate'
import { EChartType, EDashboardType, EPeriodType } from 'enums/common'
import { EKPIUnit } from 'enums/kpi'
import useNotification from 'hooks/useNotification'
import { IDashboardSetting } from 'models/dashboard'
import { IMetadata, IPieChartColor } from 'models/dashboardSetting'
import { memo, useEffect, useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { getDashboardList } from 'services/dashboard.service'
import { getDepartmentList } from 'services/department.service'
import { getKPIListOptions } from 'services/kpi.service'
import { getStaffList } from 'services/staff.service'
import { useAppDispatch, useAppSelector } from 'store'
import { updateDataChartDashboard } from 'store/slices/dashboard'
import { getDashboardSettingState, updateChartSettingById } from 'store/slices/dashboardSetting'
import { cn, formatChartData, formatYAxitProps, getListOptionValue } from 'utils/common'
import NumberUtil from 'utils/number'
import BaseAutocomplete from '../BaseAutocomplete'
import BaseAreaChart from '../BaseCharts/BaseAreaChart'
import BaseBarChart from '../BaseCharts/BaseBarChart'
import BaseLineChart from '../BaseCharts/BaseLineChart'
import { BaseMultipleChart } from '../BaseCharts/BaseMultipleChart'
import BasePieChart from '../BaseCharts/BasePieChart'
import BaseSignleChart from '../BaseCharts/BaseSingleChart'
import BaseTinyBarChart from '../BaseCharts/BaseTinyBarChart'
import BaseTreeSelect from '../BaseSelect/BaseTreeSelect'
import BaseSwitch from '../BaseSwitch'

interface ICardContent {
  height?: number
  width?: number
  dataChart: {
    id: number
    [key: string]: any
    metadata: IMetadata
    startFiscalYear: number
    dashboardType: EDashboardType
    chartType: EChartType
  }
  isPreview?: boolean
  form: UseFormReturn<any, any>
  isLoadingDelete?: boolean
}

const getColors = (data: { id: number; [key: string]: any; metadata: IMetadata }) => {
  if (!data?.metadata?.color) {
    return
  }

  if (data && Object.keys(data)?.includes(EDashboardType.GROWTH_OF_ACTUAL_PERFORMANCE)) {
    const { baseline, actualPerform, target } = data.metadata.color
    return {
      baseline,
      actualPerform,
      target
    }
  }

  if (data && Object.keys(data)?.includes(EDashboardType.SINGLE_KPI)) {
    const { baseline, actualPerform, target, overall } = data.metadata.color
    return {
      baseline,
      actualPerform,
      target,
      qAVG: overall
    }
  }

  if (data && Object.keys(data)?.includes(EDashboardType.OVERALL_PERFORMANCE)) {
    const { Q1, Q2, Q3, Q4, overall } = data.metadata.color
    return { Q1, Q2, Q3, Q4, qAVG: overall }
  }

  if (data && Object.keys(data)?.includes(EDashboardType.PERFORMANCE_STATUS)) {
    const { attentionRequired, overPerformed, underPerformed } = data.metadata.color
    return { attentionRequired, overPerformed, underPerformed }
  }

  if (data && Object.keys(data)?.includes(EDashboardType.PROGRESS_GRAPH)) {
    const { progress, remaining } = data.metadata.color
    return [progress, remaining]
  }

  if (data && Object.keys(data)?.includes(EDashboardType.PIE_CHART)) {
    return data.metadata.color.PieChart
  }
}

const getDataChartByType = (data: { id: number; [key: string]: any; metadata?: IMetadata }) => {
  const dataKeys = Object.keys(data)

  for (const element of dataKeys) {
    if (Object.values(EDashboardType).includes(element as EDashboardType)) {
      if (Array.isArray(data[element])) {
        if (data[element].length) {
          return data[element]
        } else {
          return [{}]
        }
      } else {
        return data[element]
      }
    }
  }

  return null
}

const formatPriodData = (period: any) => {
  const months = period.months
  if (period.periodType === EPeriodType.QUARTERS) {
    return [...period.quarters]
  }
  if (period.periodType === EPeriodType.MONTHS) {
    return [...months]
  }
  return [period.periodType]
}

const dataLineChartFormat = (data: any) => {
  return data?.map((d: any) => {
    return {
      name: d.name,
      overPerformed: d.overPerformed,
      underPerformed: d.underPerformed,
      attentionRequired: d.attentionRequired
    }
  })
}

const dataGrowthChartFormat = (data: any) => {
  return data?.map((d: any) => {
    return {
      name: d.name,
      baseline: d.baseline,
      target: d.target,
      actualPerform: d.actualPerform
    }
  })
}

const CardContent = ({ dataChart, height, width, form, isLoadingDelete, isPreview }: ICardContent) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { dataSettings } = useAppSelector(getDashboardSettingState)
  const n = useNotification()

  const { control, watch, reset, getValues, setValue } = form

  const wDepartmentIds = watch('departmentIds')
  const wIsGrandTotal = !!watch('isGrandTotal')
  const metric = watch('metricTypes')

  const [isLineChart, setIsLineChart] = useState(dataChart.chartType === EChartType.LINE_CHART)
  const [allowFetchData, setAllowFetchData] = useState(false)
  const [hideValue, setHideValue] = useState(false)
  const [isLoadingCard, setIsLoadingCard] = useState(true)

  const [chartId, colors, chartData, quarterOptions, chartType, unit] = useMemo(() => {
    return [
      dataChart.id,
      getColors(dataChart),
      getDataChartByType(dataChart),
      selectQuarterByStartFiscalYear(dataChart.startFiscalYear),
      dataChart.chartType,
      dataChart?.unit
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataChart])

  const settings = useMemo(() => {
    const chartFilter = dataSettings.filter((data: IDashboardSetting) => data.id === chartId)
    const chartFilterFormat = chartFilter?.[0]

    return chartFilterFormat
  }, [dataSettings, chartId])

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [API_KEY.DASHBOARD_ITEM, settings],
    queryFn: async () =>
      await getDashboardList({
        data: [settings]
      }),
    onError: n.handleUseQueryError(),
    enabled: allowFetchData
  })

  const chartHeight = useMemo(() => {
    switch (dataChart?.dashboardType) {
      case EDashboardType.PROGRESS_GRAPH:
        return (height ?? 0) + 100
      case EDashboardType.MULTIPLE_KPIS:
        return (height ?? 0) + 60
      case EDashboardType.GROWTH_OF_ACTUAL_PERFORMANCE:
      case EDashboardType.SINGLE_KPI:
        return (height ?? 0) + 40
      default:
        return (height ?? 0) + 70
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataChart, height])

  useEffect(() => {
    if (settings) {
      const period = formatPriodData(settings?.period)
      reset({
        ...settings,
        period: period
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name === 'departmentIds') {
        setValue('staffIds', [])
      }
      dispatch(updateChartSettingById(getValues() as IDashboardSetting))
      setAllowFetchData(true)
      return
    })
    return () => subscription.unsubscribe()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch])

  useEffect(() => {
    if (data && !isFetching && !isLoading) {
      dispatch(updateDataChartDashboard(data[0]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (settings) {
      setIsLoadingCard(false)
    } else {
      setIsLoadingCard(true)
    }
  }, [settings])

  return (
    <div className='flex flex-col h-full'>
      {(isLoadingCard || isLoadingDelete) && (
        <>
          <Skeleton paragraph={{ rows: 0 }} className='mb-4' />
          <Skeleton paragraph={{ rows: 8 }} />
        </>
      )}
      {/* Chart filter */}
      {!isLoadingCard && !isLoadingDelete && (
        <>
          <form>
            {[EDashboardType.OVERALL_PERFORMANCE].some((v) => Object.keys(dataChart)?.includes(v)) && (
              <Flex gap={16} className='mb-4'>
                <BaseTreeSelect
                  treeData={OVERALL_PERFORMANCE_TREE_OPTIONS}
                  name={'period'}
                  control={control}
                  treeCheckable={true}
                  placeholder={t('common.period')}
                  className={'w-56'}
                />
                <BaseAutocomplete
                  mode='multiple'
                  placeholder={t('common.department')}
                  className={'w-56'}
                  optionsKey={[API_KEY.DEPARTMENT_LIST, { isGetAll: true }]}
                  control={control}
                  allowSelectAll
                  name='departmentIds'
                  getOptionsList={getDepartmentList}
                />
                <BaseAutocomplete
                  mode='multiple'
                  placeholder={t('common.staff')}
                  className={'w-56'}
                  control={control}
                  allowSelectAll
                  name='staffIds'
                  optionsKey={[
                    API_KEY.STAFF_LIST,
                    { departmentIds: getListOptionValue(wDepartmentIds), isGetAll: true, status: true }
                  ]}
                  getOptionsList={getStaffList}
                />
              </Flex>
            )}
            {[EDashboardType.MULTIPLE_KPIS].some((v) => Object.keys(dataChart)?.includes(v)) && (
              <Flex gap={16} className='mb-4'>
                <BaseTreeSelect
                  treeData={[...MULTIPLE_TREE_OPTIONS, quarterOptions]}
                  name={'period'}
                  control={control}
                  treeCheckable={true}
                  placeholder={t('common.period')}
                  className={'w-56'}
                />
                <BaseAutocomplete
                  placeholder={t('common.department')}
                  className={'w-56'}
                  optionsKey={[API_KEY.DEPARTMENT_LIST, { isGetAll: true }]}
                  control={control}
                  name='departmentIds'
                  getOptionsList={getDepartmentList}
                />
                <BaseAutocomplete
                  placeholder={t('common.staff')}
                  className={'w-56'}
                  control={control}
                  name='staffIds'
                  optionsKey={[
                    API_KEY.STAFF_LIST,
                    { departmentIds: getListOptionValue([wDepartmentIds]), isGetAll: true, status: true }
                  ]}
                  getOptionsList={wDepartmentIds && getStaffList}
                />
              </Flex>
            )}

            {[EDashboardType.SINGLE_KPI].some((v) => Object.keys(dataChart)?.includes(v)) && (
              <Flex justify='space-between' className={cn({ 'flex-col': isPreview })}>
                <Flex className='mb-4 justify-between flex-wrap gap-4'>
                  <BaseTreeSelect
                    treeData={[...SINGLE_TREE_OPTIONS, quarterOptions]}
                    name={'period'}
                    control={control}
                    treeCheckable={true}
                    placeholder={t('common.period')}
                    className={cn('w-56', {
                      'w-52': isPreview
                    })}
                  />
                  <BaseAutocomplete
                    placeholder={t('common.kpi')}
                    className={cn('w-56', {
                      'w-52': isPreview
                    })}
                    control={control}
                    optionsKey={[
                      API_KEY.KPI_LIST,
                      { departmentIds: getListOptionValue([wDepartmentIds]), isGetAll: true }
                    ]}
                    name='kpiIds'
                    getOptionsList={getKPIListOptions}
                  />
                  <BaseAutocomplete
                    mode='multiple'
                    placeholder={t('common.metric')}
                    className={cn('w-56', {
                      'w-52': isPreview
                    })}
                    control={control}
                    name='metricTypes'
                    listOption={METRIC_OPTIONS}
                    isSort={false}
                  />
                  {width && width < 10 && (
                    <Flex className='flex-1 justify-end'>
                      <BaseSwitch
                        name={''}
                        className='flex items-center'
                        label={t('super_admin.dashboard.grand_total')}
                        value={wIsGrandTotal}
                        onChange={(e) => {
                          setValue('isGrandTotal', e)
                        }}
                      />
                    </Flex>
                  )}
                </Flex>
                {!(width && width < 10) && (
                  <Flex className={cn({ 'justify-end': isPreview })}>
                    <BaseSwitch
                      name={''}
                      className='flex items-center'
                      label={t('super_admin.dashboard.grand_total')}
                      value={wIsGrandTotal}
                      onChange={(e) => {
                        setValue('isGrandTotal', e)
                      }}
                    />
                  </Flex>
                )}
              </Flex>
            )}
            {dataChart && Object.keys(dataChart)?.includes(EDashboardType.PIE_CHART) && (
              <Flex justify='space-between' className='mb-4'>
                <BaseTreeSelect
                  treeData={[...PIE_TREE_OPTIONS, quarterOptions]}
                  name={'period'}
                  control={control}
                  treeCheckable={true}
                  placeholder={t('common.period')}
                  className={'w-56'}
                />
              </Flex>
            )}
            {dataChart && Object.keys(dataChart)?.includes(EDashboardType.GROWTH_OF_ACTUAL_PERFORMANCE) && (
              <>
                <Flex
                  justify='space-between'
                  className={cn('mb-4 gap-4', {
                    'flex-col': width && width < 10
                  })}
                >
                  <Flex justify='space-between'>
                    <div className='text-sm font-medium'>
                      <p>{t('super_admin.dashboard.total_value')}</p>
                      <p className='flex items-center'>
                        <span className='text-2xl font-[650] mr-3'>
                          {NumberUtil.format(dataChart?.finalValue || 0, {
                            unit: unit as EKPIUnit
                          })}
                        </span>
                        {dataChart?.finalPreviousValue !== undefined && (
                          <span className='bg-neutral-600 rounded-md flex gap-1 p-2 items-center whitespace-nowrap'>
                            {dataChart.finalPreviousValue > 0 ? <IconValueUp /> : <IconValueDown />}
                            {`${dataChart.finalPreviousValue}%${
                              dataChart?.titlePreviousPeriod !== undefined
                                ? ' vs. ' + dataChart.titlePreviousPeriod
                                : ''
                            }
                           `}
                          </span>
                        )}
                      </p>
                    </div>
                    {width && width < 10 && (
                      <BaseSwitch
                        name={''}
                        className='flex items-center'
                        label={t('super_admin.dashboard.line_chart')}
                        value={isLineChart}
                        onChange={(e) => {
                          setIsLineChart(e)
                          if (e) {
                            setValue('chartType', EChartType.LINE_CHART)
                          } else {
                            setValue('chartType', EChartType.STACKED_AREA)
                          }
                        }}
                      />
                    )}
                  </Flex>
                  <Flex className='items-center flex-wrap gap-4 justify-between'>
                    <BaseTreeSelect
                      treeData={[...GROWTH_PERFORMANCE_TREE_OPTIONS, quarterOptions]}
                      name={'period'}
                      control={control}
                      treeCheckable={true}
                      placeholder={t('common.period')}
                      className={cn('w-56', { 'w-48': isPreview })}
                    />
                    <BaseAutocomplete
                      mode='multiple'
                      placeholder={t('common.metric')}
                      className={cn('w-56', { 'w-48': isPreview })}
                      control={control}
                      name='metricTypes'
                      listOption={METRIC_OPTIONS}
                      isSort={false}
                    />
                    {!isPreview && !(width && width < 10) && (
                      <BaseSwitch
                        name={''}
                        className='flex items-center'
                        label={t('super_admin.dashboard.line_chart')}
                        value={isLineChart}
                        onChange={(e) => {
                          setIsLineChart(e)
                          if (e) {
                            setValue('chartType', EChartType.LINE_CHART)
                          } else {
                            setValue('chartType', EChartType.STACKED_AREA)
                          }
                        }}
                      />
                    )}
                  </Flex>
                </Flex>
                {isPreview && (
                  <Flex className='justify-end mb-3'>
                    <BaseSwitch
                      name={''}
                      className='flex items-center'
                      label={t('super_admin.dashboard.line_chart')}
                      value={isLineChart}
                      onChange={(e) => {
                        setIsLineChart(e)
                        if (e) {
                          setValue('chartType', EChartType.LINE_CHART)
                        } else {
                          setValue('chartType', EChartType.STACKED_AREA)
                        }
                      }}
                    />
                  </Flex>
                )}
              </>
            )}
            {dataChart && Object.keys(dataChart)?.includes(EDashboardType.PERFORMANCE_STATUS) && (
              <Flex justify='space-between' className='mb-4 items-center'>
                <BaseTreeSelect
                  treeData={PERFORMANCE_STATUS_TREE_OPTIONS}
                  name={'period'}
                  control={control}
                  treeCheckable={true}
                  placeholder={t('common.period')}
                  className={'w-56'}
                />
                <BaseSwitch
                  name={''}
                  className='flex items-center'
                  label={t('super_admin.dashboard.hide_value')}
                  value={hideValue}
                  onChange={(e) => setHideValue(e)}
                />
              </Flex>
            )}
          </form>
          {/* Chart view */}
          <div
            style={{ height: height ? `calc(${chartHeight}px - 150px)` : '100%', overflow: 'hidden' }}
            className={cn('multi-card', {
              'h-full':
                isPreview &&
                [EDashboardType.MULTIPLE_KPIS, EDashboardType.PROGRESS_GRAPH].includes(dataChart?.dashboardType)
            })}
          >
            {dataChart && Object.keys(dataChart)?.includes(EDashboardType.OVERALL_PERFORMANCE) && (
              <BaseBarChart
                w={width}
                data={chartData}
                chartProps={formatChartData(chartData)}
                yAxisProps={{ data: { ...formatYAxitProps(chartData) }, period: getValues('period') }}
                colors={colors}
                layout={chartType === EChartType.HORIZONTAL ? 'horizontal' : 'vertical'}
              />
            )}
            {dataChart && Object.keys(dataChart)?.includes(EDashboardType.SINGLE_KPI) && (
              <BaseSignleChart
                unit={unit}
                data={chartData}
                w={width}
                chartProps={formatChartData(chartData)}
                colors={colors}
                layout={chartType === EChartType.HORIZONTAL ? 'horizontal' : 'vertical'}
              />
            )}
            {dataChart && Object.keys(dataChart)?.includes(EDashboardType.PROGRESS_GRAPH) && (
              <BaseTinyBarChart data={chartData} colors={colors} />
            )}
            {dataChart && Object.keys(dataChart)?.includes(EDashboardType.PERFORMANCE_STATUS) && (
              <BaseLineChart
                data={chartData}
                chartProps={formatChartData(dataLineChartFormat(chartData))}
                colors={colors}
                hideValue={hideValue}
              />
            )}
            {dataChart && Object.keys(dataChart)?.includes(EDashboardType.GROWTH_OF_ACTUAL_PERFORMANCE) && (
              <BaseAreaChart
                unit={unit}
                w={width}
                data={chartData}
                chartProps={formatChartData(dataGrowthChartFormat(chartData))}
                isLineChart={isLineChart}
                colors={colors}
                metric={metric}
              />
            )}
            {dataChart && Object.keys(dataChart)?.includes(EDashboardType.PIE_CHART) && (
              <BasePieChart data={chartData} colors={colors as IPieChartColor[]} />
            )}
            {dataChart && Object.keys(dataChart)?.includes(EDashboardType.MULTIPLE_KPIS) && (
              <BaseMultipleChart data={chartData} />
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default memo(CardContent)
